"use client"

import Link from "next/link"
import { signOut, useSession } from "next-auth/react"
import { usePathname } from "next/navigation"
import {
  LayoutDashboard,
  LogOut,
  Menu,
  UserPlus,
  UserRound,
  X,
} from "lucide-react"
import { useState } from "react"
import styles from "./site-shell.module.css"

const NAV_ITEMS = [
  { href: "/play", label: "Play" },
  { href: "/daily", label: "Daily" },
  { href: "/versus", label: "Versus" },
  { href: "/custom", label: "Custom" },
  { href: "/tools", label: "Tools" },
] as const

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`)
}

export function Header() {
  const { data: session } = useSession()
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const accountName =
    session?.user.name || session?.user.email?.split("@")[0] || "Account"

  return (
    <header className={styles.header} data-site-header>
      <div className={styles.headerInner}>
        <Link href="/" className={styles.brand}>
          <span>Higher</span>
          <strong>Lower</strong>
        </Link>

        <nav className={styles.desktopNav} aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => {
            const active = isActivePath(pathname, item.href)
            return (
              <Link
                href={item.href}
                key={item.href}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className={styles.desktopAccount}>
          {session ? (
            <>
              {session.user.role === "admin" ||
              session.user.role === "owner" ? (
                <Link
                  href="/admin"
                  className={styles.iconAction}
                  aria-label="Open admin dashboard"
                  title="Admin dashboard"
                >
                  <LayoutDashboard aria-hidden="true" />
                </Link>
              ) : null}
              <span className={styles.userPill}>
                <UserRound aria-hidden="true" />
                <span>{accountName}</span>
              </span>
              <button
                type="button"
                className={styles.iconAction}
                onClick={() => signOut()}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className={styles.signInLink}>
                Sign in
              </Link>
              <Link href="/register" className={styles.accountCta}>
                Register
                <UserPlus aria-hidden="true" />
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          className={styles.mobileTrigger}
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-expanded={mobileMenuOpen}
          aria-controls="site-mobile-navigation"
          aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
        >
          {mobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>

        {mobileMenuOpen ? (
          <div className={styles.mobilePanel} id="site-mobile-navigation">
            <nav aria-label="Mobile navigation">
              {NAV_ITEMS.map((item) => {
                const active = isActivePath(pathname, item.href)
                return (
                  <Link
                    href={item.href}
                    key={item.href}
                    aria-current={active ? "page" : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            <div className={styles.mobileAccount}>
              {session ? (
                <>
                  <span>
                    <UserRound aria-hidden="true" />
                    {accountName}
                  </span>
                  {session.user.role === "admin" ||
                  session.user.role === "owner" ? (
                    <Link
                      href="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin dashboard
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false)
                      signOut()
                    }}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    href="/register"
                    className={styles.mobileAccountCta}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Create account
                    <UserPlus aria-hidden="true" />
                  </Link>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </header>
  )
}
